import { useEffect, useRef, useState } from "react";
import Alert from "@amzn/meridian/esm/components/alert";
import Link from "@amzn/meridian/esm/components/link";
import Toaster from "@amzn/meridian/esm/components/toaster";
import LogManager from "../../utils/logManager";

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? match[1] : null;
};

const setCookie = (name, value) => {
  document.cookie = `${name}=${value}; path=/; Secure; SameSite=Strict; Max-Age=31536000`;
};

const ACTION_TOKEN = "{{action}}";

/**
 * Reusable dismissible announcement toast for feature discovery.
 * Renders a Meridian Alert inside a Toaster, dismissed via cookie
 * (persists across sessions). Emits impression, dismiss, and
 * action-click metrics.
 *
 * The announcement object defines the full message via `lines` — an array
 * of strings joined by <br/>. Use {{action}} in any line to place the
 * clickable link (whose text comes from `actionLabel`).
 *
 * Callers own theming and positioning — wrap in <Theme> for link color
 * and a position:relative ancestor for Toaster placement.
 *
 * @param {Object}   announcement — { dismissKey, metricPrefix, expiresAt, lines, actionLabel }
 * @param {function} [onAction]   — callback when the action link is clicked
 * @param {boolean}  visible      — external gate (feature flag, etc.)
 */
const AnnouncementToast = ({ announcement, onAction, visible = true }) => {
  const { dismissKey, metricPrefix, expiresAt, lines, actionLabel } = announcement;
  const hasAction = Boolean(actionLabel && onAction);
  const isExpired = expiresAt && Date.now() > expiresAt;
  const impressionEmitted = useRef(false);
  const [dismissed, setDismissed] = useState(
    () => getCookie(dismissKey) === "true"
  );

  useEffect(() => {
    if (!visible || dismissed || isExpired || impressionEmitted.current) return;
    impressionEmitted.current = true;
    LogManager.metric(`${metricPrefix}_Impression`, 1);
  }, [visible, dismissed, isExpired, metricPrefix]);

  if (!visible || dismissed || isExpired) return null;

  const dismiss = () => {
    setCookie(dismissKey, "true");
    setDismissed(true);
  };

  const handleDismiss = () => {
    LogManager.metric(`${metricPrefix}_Dismiss`, 1);
    dismiss();
  };

  const handleAction = () => {
    LogManager.metric(`${metricPrefix}_ActionClick`, 1);
    dismiss();
    if (onAction) onAction();
  };

  const renderMessage = () => lines.flatMap((line, lineIndex) => {
    const content = [];
    if (lineIndex > 0) {
      content.push(<br key={`break-${lineIndex}`} />);
    }

    const [beforeAction, afterAction] = line.split(ACTION_TOKEN);
    content.push(beforeAction);
    if (afterAction !== undefined) {
      if (hasAction) {
        content.push(
          <Link key={`action-${lineIndex}`} onClick={handleAction}>
            {actionLabel}
          </Link>
        );
      } else if (actionLabel) {
        content.push(actionLabel);
      }
      content.push(afterAction);
    }
    return content;
  });

  const toasts = [{ id: dismissKey }];

  return (
    <Toaster toasts={toasts} onCloseToast={handleDismiss} position="absolute">
      {(toast) => (
        <Alert type="theme" size="medium" toast={true} onClose={toast.onClose}>
          {renderMessage()}
        </Alert>
      )}
    </Toaster>
  );
};

export default AnnouncementToast;
